import { apiConfig, http } from "@api/base";
import { getSearchTotal, prepareSumQuery } from "@api/utils";
import { sessionManager } from "@authentication/services/SessionManager";
import _isEmpty from "lodash/isEmpty";
import { generatePath } from "react-router-dom";
import { serializer } from "./serializers";

const apiPaths = {
  list: "/announcements/",
  item: "/announcements/:announcementId",
  create: "/announcements/",
  update: "/announcements/:announcementId",
  delete: "/announcements/:announcementId",
  togglePin: "/announcements/:announcementId/toggle-pin",
  byTargetGroup: "/announcements/by-target-group/:targetGroup",
  pinned: "/announcements/pinned",
};

const get = async (announcementId) => {
  const path = generatePath(apiPaths.item, { announcementId: announcementId });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const create = async (announcementData) => {
  const currentUser = sessionManager.user;

  const backendPayload = serializer.toJSON(announcementData);
  backendPayload.created_by = currentUser?.username || currentUser?.email;
  backendPayload.posted_by_fullname =
    currentUser?.fullName || currentUser?.name || "Unknown User";

  const response = await http.post(apiPaths.create, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const update = async (announcementId, announcementData) => {
  const currentUser = sessionManager.user;
  const path = generatePath(apiPaths.update, {
    announcementId: announcementId,
  });

  const backendPayload = serializer.toJSON(announcementData);
  backendPayload.updated_by = currentUser?.username || currentUser?.email;

  const response = await http.put(path, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const deleteAnnouncement = async (announcementId) => {
  const path = generatePath(apiPaths.delete, {
    announcementId: announcementId,
  });
  const response = await http.delete(path);
  return response;
};

const togglePin = async (announcementId) => {
  const path = generatePath(apiPaths.togglePin, {
    announcementId: announcementId,
  });
  const response = await http.post(path);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const getByTargetGroup = async (targetGroup) => {
  const path = generatePath(apiPaths.byTargetGroup, {
    targetGroup: targetGroup,
  });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    const announcements = Array.isArray(response.data.data)
      ? response.data.data
      : [];
    response.data.hits = announcements.map((item) => serializer.fromJSON(item));
    response.data.total = response.data.total || announcements.length;
  }

  return response;
};

const getPinned = async () => {
  const response = await http.get(apiPaths.pinned);

  if (response.data.status === "success" && response.data.data) {
    const announcements = Array.isArray(response.data.data)
      ? response.data.data
      : [];
    response.data.hits = announcements.map((item) => serializer.fromJSON(item));
    response.data.total = response.data.total || announcements.length;
  }

  return response;
};

class QueryBuilder {
  constructor() {
    this.titleQuery = [];
    this.targetGroupQuery = [];
    this.pinnedQuery = [];
    this.createdByQuery = [];
    this.size = "";
    this.page = "";
    this.sortBy = "";
  }

  withTitle(title) {
    if (!title || _isEmpty(title)) {
      throw TypeError("Title argument missing");
    }
    this.titleQuery.push(`title:"${prepareSumQuery(title)}"`);
    return this;
  }

  withTargetGroup(targetGroup) {
    if (!targetGroup || _isEmpty(targetGroup)) {
      throw TypeError("Target group argument missing");
    }
    this.targetGroupQuery.push(
      `target_group:"${prepareSumQuery(targetGroup)}"`
    );
    return this;
  }

  withCreatedBy(username) {
    if (!username || _isEmpty(username)) {
      throw TypeError("Created by argument missing");
    }
    this.createdByQuery.push(`created_by:"${prepareSumQuery(username)}"`);
    return this;
  }

  pinnedOnly() {
    this.pinnedQuery.push("is_pinned:true");
    return this;
  }

  withSize(size) {
    if (size > 0) this.size = `&size=${size}`;
    return this;
  }

  withPage(page = 0) {
    if (page > 0) this.page = `&page=${page}`;
    return this;
  }

  sortByNewest() {
    this.sortBy = `&sort=-created_at`;
    return this;
  }

  sortByOldest() {
    this.sortBy = `&sort=created_at`;
    return this;
  }

  sortByTitle() {
    this.sortBy = `&sort=title`;
    return this;
  }

  qs() {
    const searchCriteria = this.titleQuery
      .concat(this.targetGroupQuery, this.pinnedQuery, this.createdByQuery)
      .join(" AND ");
    return `${searchCriteria}${this.sortBy}${this.size}${this.page}`;
  }
}

const queryBuilder = () => {
  return new QueryBuilder();
};

const list = async (query = "") => {
  const queryString = query ? `?q=${query}` : "";
  const response = await http.get(`${apiPaths.list}${queryString}`);

  if (response.data.status === "success" && response.data.data) {
    const announcements = Array.isArray(response.data.data)
      ? response.data.data
      : [];
    response.data.hits = announcements.map((item) => serializer.fromJSON(item));
    response.data.total = response.data.total || announcements.length;
  } else if (response.data.hits) {
    response.data.total = getSearchTotal(response.data.hits);
    response.data.hits = response.data.hits.hits.map((hit) =>
      serializer.fromJSON(hit)
    );
  } else if (response.data.data) {
    response.data.hits = response.data.data.map((item) =>
      serializer.fromJSON(item)
    );
    response.data.total = response.data.total || response.data.data.length;
  } else {
    response.data.hits = [];
    response.data.total = 0;
  }

  return response;
};

const count = async (query) => {
  const response = await http.get(`${apiPaths.list}?q=${query}`);
  response.data = getSearchTotal(response.data.hits);
  return response;
};

const getCurrentUserAnnouncements = async () => {
  const currentUser = sessionManager.user;
  const username = currentUser?.username || currentUser?.email;

  if (!username) {
    return { data: { hits: [], total: 0 } };
  }

  const query = queryBuilder().withCreatedBy(username).sortByNewest().qs();

  return await list(query);
};

const getAnnouncementsByTargetGroup = async (targetGroup) => {
  const query = queryBuilder().withTargetGroup(targetGroup).sortByNewest().qs();

  return await list(query);
};

export const announcementApi = {
  searchBaseURL: `${apiConfig.baseURL}${apiPaths.list}`,
  query: queryBuilder,
  list: list,
  get: get,
  create: create,
  update: update,
  delete: deleteAnnouncement,
  togglePin: togglePin,
  count: count,
  getByTargetGroup: getByTargetGroup,
  getPinned: getPinned,
  getCurrentUserAnnouncements: getCurrentUserAnnouncements,
  getAnnouncementsByTargetGroup: getAnnouncementsByTargetGroup,
  serializer: serializer,
};
